/**
 * What the "one more detail for <app>" form does (Integrations part 1, PV13, 2026-10-11).
 *
 * Moved verbatim out of src/services/composio/panel/SetupFieldsDialog.tsx: when the server sends help, the
 * pathway is asked first and the fields show only for a pathway that can proceed; choosing a pathway clears a
 * note that was showing; a typed value is handed to the host as the next prompt. The form draws, on the
 * Integrations page and for the app-wide setup host alike; the new design's will read the same hook.
 *
 * Its frame is handed one steady close that always calls the host's latest cancel. The frame sets itself up again,
 * and takes the focus back to its card, whenever its close changes, and both hosts hand a new cancel on every render,
 * which is every typed character: the person could type one character before the focus left the field (found in
 * Integrations part 1, 2026-10-11).
 */
import { useCallback, useRef } from 'react';
import type { ConnectInitField, ConnectToolkitHelp } from '../../services/composio/ComposioClient';

export interface SetupPromptState {
  slug: string;
  fields: ConnectInitField[];
  values: Record<string, string>;
  note?: string;
  submitting?: boolean;
  /** Account-type triage (2026-08-17): when present, the dialog opens with
   *  a chooser; fields render only for the chosen pathway when it can
   *  actually proceed (showFields). */
  help?: ConnectToolkitHelp;
  pathwayId?: string;
}

export function useSetupFieldsDialog({ prompt, onChange, onCancel }: {
  prompt: SetupPromptState;
  onChange: (next: SetupPromptState) => void;
  onCancel: () => void;
}) {
  const pathway = prompt.help?.pathways.find((p) => p.id === prompt.pathwayId) || null;
  const showFields = !prompt.help || Boolean(pathway?.showFields);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const close = useCallback(() => cancelRef.current(), []);
  return {
    pathway,
    showFields,
    /** The frame's close (Escape, the backdrop): the same function every render, calling the host's latest cancel. */
    close,
    choosePathway: (pathwayId: string) => onChange({ ...prompt, pathwayId, note: undefined }),
    setValue: (name: string, value: string) => onChange({ ...prompt, values: { ...prompt.values, [name]: value } }),
  };
}
